"use client";

import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Mail, User as UserIcon } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { z } from "zod";
import { toast } from "sonner";
import ForgotPasswordButton from "../password/forgot-password-btn";
import ResendEmailButton from "../resend-email-button";
import PasswordInput from "../password/password-input";
import PasswordStrengthInput from "../password/password-strength-input";

type AuthMode = "login" | "register";

export interface AuthSubmitPayload {
  mode: AuthMode;
  email: string;
  password: string;
  name: string;
  surname: string;
}

type Props = {
  isLogin: boolean;
  onSubmit: (payload: AuthSubmitPayload) => Promise<boolean> | boolean;
};

const loginSchema = z.object({
  email: z.string().email({ message: "Email non valida" }),
  password: z.string().min(1, { message: "Inserisci la password" }),
});

const registerSchema = z.object({
  name: z.string().trim().min(3, { message: "Il nome deve avere almeno 3 caratteri" }),
  surname: z.string().trim().min(3, { message: "Il cognome deve avere almeno 3 caratteri" }),
  email: z.string().email({ message: "Email non valida" }),
  password: z.string()
    .min(8, { message: "La password deve essere di almeno 8 caratteri" })
    .regex(/[A-Z]/, { message: "La password deve contenere almeno una maiuscola" })
    .regex(/\d/, { message: "La password deve contenere almeno un numero" }),
});

export default function AuthForm({ isLogin, onSubmit }: Props) {
  const [mode, setMode] = useState<AuthMode>(isLogin ? "login" : "register");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [surname, setSurname] = useState("");
  const [errors, setErrors] = useState<{ name?: string; email?: string; password?: string; surname?: string; }>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setMode(isLogin ? "login" : "register");
  }, [isLogin]);

  const validate = () => {
    if (mode === "login") {
      const parsed = loginSchema.safeParse({ email, password });
      if (!parsed.success) {
        const formErrors: Record<string, string> = {};
        for (const issue of parsed.error.issues) formErrors[issue.path[0] as string] = issue.message;
        setErrors(formErrors);
        return false;
      }
      setErrors({});
      return true;
    } else {
      const parsed = registerSchema.safeParse({ name, surname, email, password });
      if (!parsed.success) {
        const formErrors: Record<string, string> = {};
        for (const issue of parsed.error.issues) formErrors[issue.path[0] as string] = issue.message;
        setErrors(formErrors);
        return false;
      }
      setErrors({});
      return true;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const ok = await onSubmit({
        mode,
        email: email.trim(),
        password,
        name: name.trim(),
        surname: surname.trim(),
      });
      if (mode === "register" && ok !== false) {
        setEmail("");
        setPassword("");
        setName("");
        setSurname("");
        setErrors({});
      }
      if (mode === "login" && ok === false) {
        setPassword("");
      }
    } catch {
      toast.error("Errore durante l'operazione");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleMode = (newMode: AuthMode) => {
    if (newMode !== mode) setMode(newMode);
  };

  // Transizioni morbide (solo fade) evitano ricalcoli bruschi del layout
  const fade = {
    initial: { opacity: 0 },
    animate: { opacity: 1, transition: { duration: 0.18 } },
    exit: { opacity: 0, transition: { duration: 0.14 } },
  };

  return (
    <div className="md:w-1/2 w-full p-8 md:p-16 flex flex-col items-center justify-center">
      <div className="w-full">
        <div className="max-w-md w-full mx-auto">
          <AnimatePresence mode="wait">
            <motion.h2
              key={mode}
              className="text-3xl font-bold mb-2"
              {...fade}
              style={{ willChange: "opacity" }}
            >
              {mode === "login" ? (
                <>
                  <span className="text-(--foreground)">Accedi al </span>
                  <span className="text-(--card-foreground)">tuo account!</span>
                </>
              ) : (
                <>
                  <span className="text-(--foreground)">Crea un </span>
                  <span className="text-(--card-foreground)">nuovo account!</span>
                </>
              )}
            </motion.h2>
          </AnimatePresence>

          <p className="text-sm text-gray-500 mb-2">
            {mode === "login"
              ? "Effettua il login con email e password"
              : "Registrati con email e password. Dopo la verifica email, la scuola dovrà approvare l'accesso."}
          </p>

          {/* Toggle */}
          <div className="flex mb-6 border rounded-md overflow-hidden w-fit">
            <Button
              type="button"
              onClick={() => handleToggleMode("login")}
              className={`cursor-pointer rounded-none px-6 py-2 font-medium transition-all ${
                mode === "login"
                  ? "bg-(--btn-bg) text-(--btn-foreground) hover:bg-(--btn-hover) hover:shadow-lg"
                  : "bg-white text-[#10b0ff] hover:bg-[#f0f8ff] hover:shadow-lg"
              }`}
            >
              Login
            </Button>
            <Button
              type="button"
              onClick={() => handleToggleMode("register")}
              className={`cursor-pointer rounded-none px-6 py-2 font-medium transition-all ${
                mode === "register"
                  ? "bg-(--btn-bg) text-(--btn-foreground) hover:bg-(--btn-hover) hover:shadow-lg"
                  : "bg-white text-[#10b0ff] hover:bg-[#f0f8ff] hover:shadow-lg"
              }`}
            >
              Registrati
            </Button>
          </div>

          {/* Form: niente slide/absolute, solo fade */}
          <AnimatePresence mode="wait">
            <motion.form
              key={mode}
              {...fade}
              onSubmit={handleSubmit}
              className="space-y-3"
              style={{ willChange: "opacity" }}
              autoComplete={mode === "login" ? "on" : "off"}
            >
              {/* Nome/Cognome: sempre montati, animiamo height/opacity come accordion */}
              <motion.div
                initial={false}
                animate={
                  mode === "register"
                    ? { height: "auto", opacity: 1 }
                    : { height: 0, opacity: 0 }
                }
                transition={{ duration: 0.22, ease: "easeInOut" }}
                style={{ overflow: "hidden" }}
              >
                <div className="space-y-3 pt-1">
                  <div className="relative">
                    <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-(--card-foreground) w-4 h-4" />
                    <Input
                      type="text"
                      name="given-name"
                      autoComplete="given-name"
                      placeholder="Nome"
                      className="pl-10 text-(--foreground)"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      aria-invalid={!!errors.name}
                      aria-describedby={errors.name ? "name-error" : undefined}
                    />
                    {errors.name && (
                      <p id="name-error" className="text-red-500 text-sm mt-1">{errors.name}</p>
                    )}
                  </div>

                  <div className="relative">
                    <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-(--card-foreground) w-4 h-4" />
                    <Input
                      type="text"
                      name="family-name"
                      autoComplete="family-name"
                      placeholder="Cognome"
                      className="pl-10 text-(--foreground)"
                      value={surname}
                      onChange={(e) => setSurname(e.target.value)}
                      aria-invalid={!!errors.surname}
                      aria-describedby={errors.surname ? "surname-error" : undefined}
                    />
                    {errors.surname && (
                      <p id="surname-error" className="text-red-500 text-sm mt-1">{errors.surname}</p>
                    )}
                  </div>
                </div>
              </motion.div>

              {/* Email (username per autofill password manager) */}
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-(--card-foreground) w-4 h-4" />
                <Input
                  type="email"
                  name={mode === "login" ? "username" : "email"}
                  autoComplete={mode === "login" ? "username email" : "email"}
                  placeholder="Email"
                  className="pl-10 text-(--foreground)"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  aria-invalid={!!errors.email}
                  aria-describedby={errors.email ? "email-error" : undefined}
                />
              </div>
              {errors.email && (
                <p id="email-error" className="text-red-500 text-sm -mt-2">{errors.email}</p>
              )}

              {/* Password */}
              {mode === "register" ? (
                <PasswordStrengthInput value={password} onChange={setPassword} />
              ) : (
                <PasswordInput
                  value={password}
                  onChange={setPassword}
                  name="password"
                  autoComplete="current-password"
                  required
                  aria-invalid={!!errors.password}
                  aria-describedby={errors.password ? "password-error" : undefined}
                />
              )}
              {errors.password && (
                <p id="password-error" className="text-red-500 text-sm -mt-2">{errors.password}</p>
              )}

              <div className="flex flex-col items-start space-y-4">
                {mode === "login" && <ForgotPasswordButton />}
                {mode === "login" && <ResendEmailButton initialEmail={email} />}

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-[200px] bg-(--btn-bg) text-(--btn-foreground) cursor-pointer rounded-lg px-6 py-2 text-md font-semibold transition-all hover:bg-(--btn-hover)"
                >
                  {loading ? "Invio in corso…" : mode === "login" ? "Login" : "Registrati"}
                </Button>
              </div>
            </motion.form>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}